import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import SignaturePad from "signature_pad";
import { useAuth } from "@/contexts/AuthContext";
import { generatePdfFromHtml } from "@/utils/pdf-generator";
import { auth, db } from "@/lib/firebase";
import { MOCK_LISTINGS } from "@/data/listings.data";
import { getListingBySlug } from "@/data/listing-details.data";
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { invokeFunction, invokeFunctionSilent } from "@/lib/cloudrun";

// Types pour les données du NDA
interface NDAData {
  dealId: string;
  dealReference: string;
  industrySector: string;
  effectiveDate: string;
  adminPartyName: string;
  hasBroker: boolean;
  brokerName?: string;
  brokerAddress?: string;
  brokerEmail?: string;
  recipientName: string;
  recipientAddress: string;
  recipientEmail: string;
  recipientPhone: string;
  recipientType: "buyer" | "lender";
  confidentialityYears: number;
  nonSolicitationYears: number;
  governingLaw: string;
  generationDate: string;
  recipientSignature?: string;
  sellerId?: string;
  sellerName?: string;
  sellerEmail?: string;
  businessName?: string;
}

interface Deal {
  id: string;
  business_name: string;
  business_description: string | null;
  broker_id: string | null;
  seller_id: string | null;
  reference_number: string | null;
}

// Données par défaut
const defaultDataFR: NDAData = {
  dealId: "",
  dealReference: "",
  industrySector: "",
  effectiveDate: new Date().toLocaleDateString('fr-CA'),
  adminPartyName: "AcquizGroup Inc.",
  hasBroker: false,
  brokerName: "",
  brokerAddress: "",
  recipientName: "",
  recipientAddress: "",
  recipientEmail: "",
  recipientPhone: "",
  recipientType: "buyer",
  confidentialityYears: 3,
  nonSolicitationYears: 2,
  governingLaw: "l'État de Floride, États-Unis",
  generationDate: new Date().toLocaleDateString('fr-CA'),
};

const defaultDataEN: NDAData = {
  dealId: "",
  dealReference: "",
  industrySector: "",
  effectiveDate: new Date().toLocaleDateString('en-US'),
  adminPartyName: "AcquizGroup Inc.",
  hasBroker: false,
  brokerName: "",
  brokerAddress: "",
  recipientName: "",
  recipientAddress: "",
  recipientEmail: "",
  recipientPhone: "",
  recipientType: "buyer",
  confidentialityYears: 3,
  nonSolicitationYears: 2,
  governingLaw: "the State of Florida, United States",
  generationDate: new Date().toLocaleDateString('en-US'),
};

// Composant pour les champs de données
const DataField: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      fontWeight: "bold",
      color: "#007bff",
      backgroundColor: "#e7f3ff",
      padding: "2px 4px",
      borderRadius: "3px",
    }}
  >
    {children || "_______________"}
  </span>
);

// Composant pour les titres de section
const SectionHeader: React.FC<{ children: React.ReactNode; highlight?: boolean }> = ({ 
  children, 
  highlight 
}) => (
  <div
    style={{
      fontSize: "18px",
      fontWeight: "bold",
      marginTop: "20px",
      borderBottom: "2px solid #ccc",
      paddingBottom: "5px",
      color: highlight ? "#8b5cf6" : undefined,
    }}
  >
    {children}
  </div>
);

// Composant pour les boîtes de partie
const PartyBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      border: "1px solid #eee",
      padding: "15px",
      marginTop: "10px",
      borderRadius: "5px",
      backgroundColor: "#fafafa",
    }}
  >
    {children}
  </div>
);

// Composant pour les boîtes de signature
const SignatureBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      border: "1px dashed #999",
      padding: "10px",
      backgroundColor: "#f9f9f9",
      fontStyle: "italic",
      color: "#666",
    }}
  >
    {children}
  </div>
);

// Formulaire de saisie des données
const DataInputForm: React.FC<{
  data: NDAData;
  setData: React.Dispatch<React.SetStateAction<NDAData>>;
  language: "fr" | "en";
  deals: Deal[];
  loadingDeals: boolean;
  onDealSelect: (dealId: string) => void;
}> = ({ data, setData, language, deals, loadingDeals, onDealSelect }) => {
  const t = language === "fr" ? {
    dealInfo: "Informations de l'Affaire",
    selectDeal: "Sélectionner un Deal",
    selectDealPlaceholder: "-- Choisir un deal pour auto-remplir --",
    dealReference: "Référence de l'Affaire",
    industrySector: "Secteur d'Activité",
    effectiveDate: "Date Effective",
    brokerInfo: "Informations du Broker (optionnel)",
    hasBroker: "Inclure un Broker",
    brokerName: "Nom du Broker",
    brokerAddress: "Adresse du Broker",
    recipientType: "Type de Récipiendaire",
    buyer: "Acheteur Potentiel",
    lender: "Prêteur",
    terms: "Termes de l'Accord",
    confidentialityYears: "Durée de confidentialité (années)",
    nonSolicitationYears: "Durée de non-sollicitation (années)",
    governingLaw: "Droit applicable",
    loading: "Chargement des deals...",
  } : {
    dealInfo: "Deal Information",
    selectDeal: "Select a Deal",
    selectDealPlaceholder: "-- Choose a deal to auto-fill --",
    dealReference: "Deal Reference",
    industrySector: "Industry Sector",
    effectiveDate: "Effective Date",
    brokerInfo: "Broker Information (optional)",
    hasBroker: "Include a Broker",
    brokerName: "Broker Name",
    brokerAddress: "Broker Address",
    recipientType: "Recipient Type",
    buyer: "Potential Buyer",
    lender: "Lender",
    terms: "Agreement Terms",
    confidentialityYears: "Confidentiality Period (years)",
    nonSolicitationYears: "Non-Solicitation Period (years)",
    governingLaw: "Governing Law",
    loading: "Loading deals...",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "4px",
    fontWeight: "500",
    fontSize: "14px",
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: "16px",
  };

  return (
    <div style={{ padding: "20px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
      {/* Deal Selection - Auto-fill from Deal Monitor */}
      <h3 style={{ marginTop: 0, marginBottom: "16px", color: "#1D314D", borderBottom: "2px solid #CEAB65", paddingBottom: "8px" }}>
        {t.dealInfo}
      </h3>
      
      <div style={fieldGroupStyle}>
        <label style={labelStyle}>{t.selectDeal}</label>
        <select
          style={{ ...inputStyle, backgroundColor: loadingDeals ? "#f0f0f0" : "#fff" }}
          value={data.dealId}
          onChange={(e) => onDealSelect(e.target.value)}
          disabled={loadingDeals}
        >
          <option value="">{loadingDeals ? t.loading : t.selectDealPlaceholder}</option>
          {deals.map((deal) => (
            <option key={deal.id} value={deal.id}>
              {deal.business_name} ({deal.id.slice(0, 8)}...)
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.dealReference}</label>
          <input
            type="text"
            style={{ ...inputStyle, backgroundColor: "#e9ecef" }}
            value={data.dealReference}
            readOnly
            placeholder="DEAL-2024-001234"
          />
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.industrySector}</label>
          <input
            type="text"
            style={inputStyle}
            value={data.industrySector}
            onChange={(e) => setData({ ...data, industrySector: e.target.value })}
            placeholder={language === "fr" ? "Services Professionnels" : "Professional Services"}
          />
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.effectiveDate}</label>
          <input
            type="date"
            style={inputStyle}
            value={data.effectiveDate}
            onChange={(e) => setData({ ...data, effectiveDate: e.target.value })}
          />
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.recipientType}</label>
          <select
            style={inputStyle}
            value={data.recipientType}
            onChange={(e) => setData({ ...data, recipientType: e.target.value as "buyer" | "lender" })}
          >
            <option value="buyer">{t.buyer}</option>
            <option value="lender">{t.lender}</option>
          </select>
        </div>
      </div>

      {/* Broker Info */}
      <h3 style={{ marginTop: "24px", marginBottom: "16px", color: "#1D314D", borderBottom: "2px solid #CEAB65", paddingBottom: "8px" }}>
        {t.brokerInfo}
      </h3>
      <div style={fieldGroupStyle}>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={data.hasBroker}
            onChange={(e) => setData({ ...data, hasBroker: e.target.checked })}
          />
          {t.hasBroker}
        </label>
      </div>
      {data.hasBroker && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t.brokerName}</label>
            <input
              type="text"
              style={inputStyle}
              value={data.brokerName}
              onChange={(e) => setData({ ...data, brokerName: e.target.value })}
            />
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t.brokerAddress}</label>
            <input
              type="text"
              style={inputStyle}
              value={data.brokerAddress}
              onChange={(e) => setData({ ...data, brokerAddress: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Recipient Info */}
      <h3 style={{ marginTop: "24px", marginBottom: "16px", color: "#1D314D", borderBottom: "2px solid #CEAB65", paddingBottom: "8px" }}>
        {language === "fr" ? "Informations du Récipiendaire" : "Recipient Information"}
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{language === "fr" ? "Téléphone" : "Phone"}</label>
          <input
            type="tel"
            style={inputStyle}
            value={data.recipientPhone}
            onChange={(e) => setData({ ...data, recipientPhone: e.target.value })}
            placeholder="+1 (555) 000-0000"
          />
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{language === "fr" ? "Adresse" : "Address"}</label>
          <input
            type="text"
            style={inputStyle}
            value={data.recipientAddress}
            onChange={(e) => setData({ ...data, recipientAddress: e.target.value })}
            placeholder={language === "fr" ? "123 Rue Principale, Ville" : "123 Main St, City"}
          />
        </div>
      </div>

      {/* Terms */}
      <h3 style={{ marginTop: "24px", marginBottom: "16px", color: "#1D314D", borderBottom: "2px solid #CEAB65", paddingBottom: "8px" }}>
        {t.terms}
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.confidentialityYears}</label>
          <select
            style={inputStyle}
            value={data.confidentialityYears}
            onChange={(e) => setData({ ...data, confidentialityYears: parseInt(e.target.value) })}
          >
            {[1, 2, 3, 4, 5].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.nonSolicitationYears}</label>
          <select
            style={inputStyle}
            value={data.nonSolicitationYears}
            onChange={(e) => setData({ ...data, nonSolicitationYears: parseInt(e.target.value) })}
          >
            {[1, 2, 3, 4, 5].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>{t.governingLaw}</label>
          <input
            type="text"
            style={inputStyle}
            value={data.governingLaw}
            onChange={(e) => setData({ ...data, governingLaw: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

// Composant SignaturePad
const SignaturePadComponent: React.FC<{
  onSignatureChange: (signature: string | undefined) => void;
  language: "fr" | "en";
}> = ({ onSignatureChange, language }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const onSignatureChangeRef = useRef(onSignatureChange);
  const [hasDraftSignature, setHasDraftSignature] = useState(false);
  const [isSignatureConfirmed, setIsSignatureConfirmed] = useState(false);

  useEffect(() => {
    onSignatureChangeRef.current = onSignatureChange;
  }, [onSignatureChange]);

  useEffect(() => {
    if (canvasRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: "rgb(255, 255, 255)",
        penColor: "rgb(0, 0, 0)",
      });

      const handleBeginStroke = () => {
        setIsSignatureConfirmed(false);
      };

      const handleEndStroke = () => {
        if (!signaturePadRef.current) return;
        const hasSignature = !signaturePadRef.current.isEmpty();
        setHasDraftSignature(hasSignature);
        setIsSignatureConfirmed(false);
      };

      signaturePadRef.current.addEventListener("beginStroke", handleBeginStroke);
      signaturePadRef.current.addEventListener("endStroke", handleEndStroke);

      // Resize canvas
      const resizeCanvas = () => {
        if (canvasRef.current && signaturePadRef.current) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvasRef.current.width = canvasRef.current.offsetWidth * ratio;
          canvasRef.current.height = canvasRef.current.offsetHeight * ratio;
          canvasRef.current.getContext("2d")?.scale(ratio, ratio);
          signaturePadRef.current.clear();
        }
      };

      resizeCanvas();
      window.addEventListener("resize", resizeCanvas);

      return () => {
        signaturePadRef.current?.removeEventListener("beginStroke", handleBeginStroke);
        signaturePadRef.current?.removeEventListener("endStroke", handleEndStroke);
        window.removeEventListener("resize", resizeCanvas);
      };
    }
  }, []);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasDraftSignature(false);
      setIsSignatureConfirmed(false);
      onSignatureChangeRef.current(undefined);
    }
  };

  const confirmSignature = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return;

    onSignatureChangeRef.current(signaturePadRef.current.toDataURL());
    setHasDraftSignature(true);
    setIsSignatureConfirmed(true);
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <p style={{ fontWeight: "bold", marginBottom: "10px" }}>
        {language === "fr" ? "Signature du Récipiendaire" : "Recipient Signature"}
      </p>
      <div
        style={{
          border: "2px solid #333",
          borderRadius: "4px",
          backgroundColor: "#fff",
          position: "relative",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "150px",
            display: "block",
          }}
        />
      </div>
      <button
        type="button"
        onClick={clearSignature}
        style={{
          marginTop: "8px",
          padding: "6px 12px",
          backgroundColor: "#dc3545",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
        {language === "fr" ? "Effacer la signature" : "Clear Signature"}
      </button>
      <button
        type="button"
        onClick={confirmSignature}
        disabled={!hasDraftSignature}
        style={{
          marginTop: "8px",
          marginLeft: "8px",
          padding: "6px 12px",
          backgroundColor: hasDraftSignature ? "#28a745" : "#ccc",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: hasDraftSignature ? "pointer" : "not-allowed",
          fontSize: "12px",
        }}
      >
        {language === "fr" ? "Confirmer cette signature" : "Confirm this signature"}
      </button>

      {hasDraftSignature && !isSignatureConfirmed && (
        <p style={{ marginTop: "10px", marginBottom: 0, fontSize: "12px", color: "#666" }}>
          {language === "fr"
            ? "Vous pouvez continuer à signer, ajouter des espaces, puis confirmer seulement quand vous êtes satisfait."
            : "You can keep signing, add spaces, and confirm only when you're satisfied."}
        </p>
      )}

      {isSignatureConfirmed && (
        <p style={{ marginTop: "10px", marginBottom: 0, fontSize: "12px", color: "#155724" }}>
          ✓ {language === "fr" ? "Signature confirmée" : "Signature confirmed"}
        </p>
      )}
    </div>
  );
};

// Document NDA en français
const NDADocumentFR: React.FC<{ data: NDAData }> = ({ data }) => (
  <div
    style={{
      fontFamily: "Arial, sans-serif",
      lineHeight: 1.6,
      padding: "40px",
      maxWidth: "900px",
      marginLeft: "auto",
      marginRight: "auto",
    }}
  >
    <div
      style={{
        fontSize: "24px",
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: "30px",
      }}
    >
      ACCORD DE NON-DIVULGATION, DE CONFIDENTIALITÉ ET DE NON-SOLLICITATION MAÎTRE
    </div>

    <p style={{ textAlign: "right", fontStyle: "italic" }}>
      <strong>Référence de l'Affaire :</strong> <DataField>{data.dealReference}</DataField>
      <br />
      <strong>Secteur d'Activité :</strong> <DataField>{data.industrySector}</DataField>
    </p>

    <p>
      Le présent Accord est conclu et prend effet à compter du{" "}
      <strong>
        <DataField>{data.effectiveDate}</DataField>
      </strong>{" "}
      (la « <strong>Date Effective</strong> ») par et entre les Parties suivantes :
    </p>

    <SectionHeader>A. Les Parties Divulguantes (Vendeur Anonyme / Agent de Liaison)</SectionHeader>

    <PartyBox>
      <p>
        <strong>Partie Divulguante I (L'Administrateur) :</strong>
      </p>
      <ul>
        <li>
          <strong>Nom</strong> : <DataField>{data.adminPartyName}</DataField> (AcquizGroup, agissant
          en tant qu'Administrateur de la Plateforme et agent de liaison autorisé par le Vendeur)
        </li>
      </ul>
      {data.hasBroker && (
        <>
          <p style={{ marginTop: "10px" }}>
            <strong>Partie Divulguante II (Le Broker, si applicable) :</strong>
          </p>
          <ul>
            <li>
              <strong>Nom</strong> : <DataField>{data.brokerName}</DataField>
            </li>
            <li>
              <strong>Adresse</strong> : <DataField>{data.brokerAddress}</DataField>
            </li>
          </ul>
        </>
      )}
      <p>
        (Collectivement les « <strong>Parties Divulguantes</strong> »)
      </p>
    </PartyBox>

    <SectionHeader>B. La Partie Récipiendaire ({data.recipientType === "lender" ? "Le Prêteur" : "L'Acheteur Potentiel"})</SectionHeader>

    <PartyBox>
      <ul>
        <li>
          <strong>Nom</strong> :{" "}
          <strong>
            <DataField>{data.recipientName}</DataField>
          </strong>{" "}
          (ci-après {data.recipientType === "lender" ? "le « Prêteur »" : "l'« Acheteur Potentiel »"})
        </li>
        <li>
          <strong>Adresse</strong> : <DataField>{data.recipientAddress}</DataField>
        </li>
        <li>
          <strong>E-mail</strong> : <DataField>{data.recipientEmail}</DataField>
        </li>
        <li>
          <strong>Téléphone</strong> : <DataField>{data.recipientPhone}</DataField>
        </li>
      </ul>
    </PartyBox>

    <SectionHeader highlight>CONSIDÉRATIONS PRÉLIMINAIRES (RÔLE ACQUIZGROUP)</SectionHeader>
    <ol>
      <li>
        L'Acheteur Potentiel désire évaluer une opportunité d'acquisition de l'entreprise identifiée
        par la Référence <DataField>{data.dealReference}</DataField> (l'« <strong>Affaire</strong>{" "}
        »).
      </li>
      <li>
        L'Acheteur Potentiel reconnaît que l'identité du Vendeur est <strong>confidentielle</strong>{" "}
        et ne sera divulguée que par écrit par AcquizGroup après l'exécution du présent Accord.
      </li>
      <li>
        L'Acheteur Potentiel reconnaît et accepte qu'
        <strong>
          AcquizGroup agit uniquement en tant que plateforme technologique et agent de liaison
        </strong>{" "}
        pour faciliter la transmission des Informations Confidentielles pour le compte du Vendeur,
        sans agir comme courtier ou conseiller dans la transaction.
      </li>
    </ol>

    <SectionHeader>1. Définitions</SectionHeader>
    <p>
      <strong>1.1. Informations Confidentielles.</strong> Désigne toutes les informations non
      publiques, commerciales, financières (incluant CA, EBITDA/SDE ajusté), opérationnelles,
      techniques, juridiques ou autres, relatives à l'Affaire, divulguées, que ce soit oralement,
      par écrit, visuellement ou électroniquement, par les Parties Divulguantes à l'Acheteur
      Potentiel.
    </p>
    <p>
      <strong>1.2. Affaire.</strong> Désigne l'opportunité d'acquisition commerciale identifiée par
      la Référence <DataField>{data.dealReference}</DataField>, dont les détails opérationnels et
      l'identité du propriétaire sont maintenus confidentiels jusqu'à divulgation expresse par
      AcquizGroup.
    </p>
    <p>
      <strong>1.3. Vendeur.</strong> Désigne le propriétaire actuel de l'entreprise faisant l'objet
      de l'Affaire, dont l'identité demeure confidentielle et sera communiquée uniquement après
      signature du présent Accord.
    </p>

    <SectionHeader>2. Obligations de Confidentialité et Durée</SectionHeader>
    <p>
      <strong>2.1. Obligation de Confidentialité.</strong> L'Acheteur Potentiel s'engage à maintenir
      la stricte confidentialité des Informations Confidentielles et à ne les utiliser que dans le
      but exclusif d'évaluer l'Affaire.
    </p>
    <p>
      <strong>2.2. Restrictions d'Utilisation.</strong> L'Acheteur Potentiel s'engage à :
    </p>
    <ul>
      <li>
        Ne pas divulguer les Informations Confidentielles à des tiers sans le consentement écrit
        préalable des Parties Divulguantes ;
      </li>
      <li>
        Limiter l'accès aux Informations Confidentielles uniquement aux employés, conseillers ou
        représentants qui ont un besoin légitime de les connaître et qui sont liés par des
        obligations de confidentialité équivalentes ;
      </li>
      <li>
        Ne pas utiliser les Informations Confidentielles à des fins autres que l'évaluation de
        l'Affaire ;
      </li>
      <li>
        Prendre toutes les mesures raisonnables pour protéger la confidentialité des Informations
        Confidentielles.
      </li>
    </ul>
    <p>
      <strong>2.3. Durée.</strong> Les obligations de confidentialité resteront en vigueur pendant
      une période de{" "}
      <strong>
        <DataField>{data.confidentialityYears}</DataField>
      </strong>{" "}
      ans à compter de la Date Effective.
    </p>

    <SectionHeader>3. Exclusions (Exceptions à la Confidentialité)</SectionHeader>
    <p>Les obligations de confidentialité ne s'appliquent pas aux informations qui :</p>
    <ol type="a">
      <li>Sont ou deviennent accessibles au public sans faute de l'Acheteur Potentiel ;</li>
      <li>
        Étaient légalement connues de l'Acheteur Potentiel sans obligation de confidentialité avant
        la Date Effective ;
      </li>
      <li>
        Sont reçues d'un tiers sans restriction de divulgation et sans violation d'une obligation de
        confidentialité ;
      </li>
      <li>
        Sont développées de manière indépendante par l'Acheteur Potentiel sans utilisation des
        Informations Confidentielles ;
      </li>
      <li>
        Doivent être divulguées en vertu d'une loi, d'un règlement ou d'une ordonnance judiciaire,
        sous réserve d'une notification préalable aux Parties Divulguantes dans la mesure du
        possible.
      </li>
    </ol>

    <SectionHeader>4. Non-Sollicitation</SectionHeader>
    <p>
      Pendant une période de{" "}
      <strong>
        <DataField>{data.nonSolicitationYears}</DataField>
      </strong>{" "}
      ans à compter de la Date Effective, l'Acheteur Potentiel s'interdit de solliciter ou
      d'engager, directement ou indirectement, tout employé, dirigeant, fournisseur ou client de
      l'Affaire, sans l'accord écrit préalable du Vendeur ou du Broker.
    </p>
    <p>
      <strong>4.1. Portée de la Non-Sollicitation.</strong> Cette clause s'applique à toute
      tentative de :
    </p>
    <ul>
      <li>Recruter ou embaucher des employés clés de l'entreprise ;</li>
      <li>Détourner des clients ou fournisseurs de l'Affaire ;</li>
      <li>
        Inciter des partenaires commerciaux à rompre leurs relations avec l'Affaire.
      </li>
    </ul>
    <p>
      <strong>4.2. Exception.</strong> Cette restriction ne s'applique pas aux sollicitations
      générales d'emploi non ciblées (annonces publiques, agences de recrutement) où l'employé
      répond de sa propre initiative.
    </p>

    <SectionHeader>5. Dispositions Générales</SectionHeader>
    <p>
      <strong>5.1. Droit Applicable.</strong> Le présent Accord sera régi et interprété conformément
      aux lois de{" "}
      <strong>
        <DataField>{data.governingLaw}</DataField>
      </strong>
      .
    </p>
    <p>
      <strong>5.2. Retour des Informations.</strong> Sur demande écrite des Parties Divulguantes,
      l'Acheteur Potentiel devra rapidement retourner ou détruire toutes les copies des Informations
      Confidentielles en sa possession, et fournir une certification écrite de cette destruction.
    </p>
    <p>
      <strong>5.3. Absence de Garantie.</strong> Les Parties Divulguantes ne font aucune déclaration
      ou garantie, expresse ou implicite, quant à l'exactitude ou l'exhaustivité des Informations
      Confidentielles. L'Acheteur Potentiel assume l'entière responsabilité de sa propre analyse et
      diligence raisonnable.
    </p>
    <p>
      <strong>5.4. Aucune Obligation de Transaction.</strong> Le présent Accord ne crée aucune
      obligation pour l'une ou l'autre des Parties de conclure une transaction. Toute transaction
      éventuelle sera soumise à la négociation et à l'exécution d'accords définitifs distincts.
    </p>
    <p>
      <strong>5.5. Recours.</strong> L'Acheteur Potentiel reconnaît qu'une violation du présent
      Accord pourrait causer un préjudice irréparable aux Parties Divulguantes et au Vendeur, et que
      des dommages-intérêts monétaires pourraient être insuffisants. En conséquence, les Parties
      Divulguantes auront le droit de demander une injonction ou d'autres recours en équité, en plus
      de tous autres recours disponibles en droit.
    </p>
    <p>
      <strong>5.6. Divisibilité.</strong> Si une disposition du présent Accord est jugée invalide ou
      inapplicable, les autres dispositions demeureront en vigueur dans toute la mesure permise par
      la loi.
    </p>
    <p>
      <strong>5.7. Accord Intégral.</strong> Le présent Accord constitue l'intégralité de l'entente
      entre les Parties concernant son objet et remplace tous accords, discussions ou ententes
      antérieurs, écrits ou oraux.
    </p>

    <SectionHeader>6. Signatures et Engagement</SectionHeader>

    <p style={{ marginTop: "20px" }}>
      <strong>EN FOI DE QUOI</strong>, les Parties ont signé le présent Accord à la date indiquée
      ci-dessous.
    </p>

    <table style={{ width: "100%", marginTop: "30px", borderCollapse: "collapse" }}>
      <tbody>
        <tr>
          <td style={{ width: "45%", verticalAlign: "top" }}>
            <div style={{ borderTop: "2px solid #333", paddingTop: "10px", marginTop: "50px" }}>
              <p style={{ fontWeight: "bold" }}>PARTIE DIVULGUANTE I (ACQUIZGROUP)</p>
              <SignatureBox>
                <p>
                  <strong>Signature :</strong> <DataField>AcquizGroup</DataField>
                </p>
                <p>
                  <strong>Titre :</strong> <DataField>Agent de liaison autorisé</DataField>
                </p>
                <p>
                  <strong>Date :</strong> <DataField>{data.generationDate}</DataField>
                </p>
              </SignatureBox>
            </div>
          </td>
          <td style={{ width: "10%" }}></td>
          <td style={{ width: "45%", verticalAlign: "top" }}>
            <div style={{ borderTop: "2px solid #333", paddingTop: "10px", marginTop: "50px" }}>
              <p style={{ fontWeight: "bold" }}>PARTIE RÉCIPIENDAIRE ({data.recipientType === "lender" ? "PRÊTEUR" : "ACHETEUR POTENTIEL"})</p>
              <SignatureBox>
                <p>
                  <strong>Signature :</strong>{" "}
                  {data.recipientSignature ? (
                    <img src={data.recipientSignature} alt="Signature" style={{ maxWidth: "200px", maxHeight: "60px" }} />
                  ) : (
                    "[En attente de signature]"
                  )}
                </p>
                <p>
                  <strong>Nom :</strong> <DataField>{data.recipientName}</DataField>
                </p>
                <p>
                  <strong>Date :</strong> <DataField>{data.generationDate}</DataField>
                </p>
              </SignatureBox>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    {data.hasBroker && (
      <div style={{ marginTop: "40px", borderTop: "1px solid #ddd", paddingTop: "20px" }}>
        <p style={{ fontWeight: "bold" }}>PARTIE DIVULGUANTE II (LE BROKER)</p>
        <div style={{ width: "45%" }}>
          <SignatureBox>
            <p>
              <strong>Signature :</strong> [En attente de signature]
            </p>
            <p>
              <strong>Nom :</strong> <DataField>{data.brokerName}</DataField>
            </p>
            <p>
              <strong>Date :</strong> [En attente]
            </p>
          </SignatureBox>
        </div>
      </div>
    )}

    <div
      style={{
        marginTop: "40px",
        backgroundColor: "#f0f0f0",
        padding: "15px",
        borderRadius: "5px",
      }}
    >
      <p style={{ fontSize: "14px", margin: 0 }}>
        <strong>🔐 Sécurité et Conformité</strong>
      </p>
      <p style={{ fontSize: "12px", margin: "5px 0 0 0" }}>
        Ce document est généré automatiquement par la plateforme AcquizGroup. Toutes les signatures
        électroniques sont juridiquement valides conformément aux lois applicables.
      </p>
    </div>
  </div>
);

// Document NDA en anglais
const NDADocumentEN: React.FC<{ data: NDAData }> = ({ data }) => (
  <div
    style={{
      fontFamily: "Arial, sans-serif",
      lineHeight: 1.6,
      padding: "40px",
      maxWidth: "900px",
      marginLeft: "auto",
      marginRight: "auto",
    }}
  >
    <div
      style={{
        fontSize: "24px",
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: "30px",
      }}
    >
      MASTER NON-DISCLOSURE, CONFIDENTIALITY AND NON-SOLICITATION AGREEMENT
    </div>

    <p style={{ textAlign: "right", fontStyle: "italic" }}>
      <strong>Deal Reference:</strong> <DataField>{data.dealReference}</DataField>
      <br />
      <strong>Industry Sector:</strong> <DataField>{data.industrySector}</DataField>
    </p>

    <p>
      This Agreement is entered into and effective as of{" "}
      <strong>
        <DataField>{data.effectiveDate}</DataField>
      </strong>{" "}
      (the "<strong>Effective Date</strong>") by and between the following Parties:
    </p>

    <SectionHeader>A. The Disclosing Parties (Anonymous Seller / Liaison Agent)</SectionHeader>

    <PartyBox>
      <p>
        <strong>Disclosing Party I (The Administrator):</strong>
      </p>
      <ul>
        <li>
          <strong>Name</strong>: <DataField>{data.adminPartyName}</DataField> (AcquizGroup, acting
          as Platform Administrator and authorized liaison agent for the Seller)
        </li>
      </ul>
      {data.hasBroker && (
        <>
          <p style={{ marginTop: "10px" }}>
            <strong>Disclosing Party II (The Broker, if applicable):</strong>
          </p>
          <ul>
            <li>
              <strong>Name</strong>: <DataField>{data.brokerName}</DataField>
            </li>
            <li>
              <strong>Address</strong>: <DataField>{data.brokerAddress}</DataField>
            </li>
          </ul>
        </>
      )}
      <p>
        (Collectively the "<strong>Disclosing Parties</strong>")
      </p>
    </PartyBox>

    <SectionHeader>B. The Receiving Party ({data.recipientType === "lender" ? "The Lender" : "The Potential Buyer"})</SectionHeader>

    <PartyBox>
      <ul>
        <li>
          <strong>Name</strong>:{" "}
          <strong>
            <DataField>{data.recipientName}</DataField>
          </strong>{" "}
          (hereinafter the "{data.recipientType === "lender" ? "Lender" : "Potential Buyer"}")
        </li>
        <li>
          <strong>Address</strong>: <DataField>{data.recipientAddress}</DataField>
        </li>
        <li>
          <strong>Email</strong>: <DataField>{data.recipientEmail}</DataField>
        </li>
        <li>
          <strong>Phone</strong>: <DataField>{data.recipientPhone}</DataField>
        </li>
      </ul>
    </PartyBox>

    <SectionHeader highlight>PRELIMINARY RECITALS (ACQUIZGROUP ROLE)</SectionHeader>
    <ol>
      <li>
        The Potential Buyer wishes to evaluate an acquisition opportunity for the business
        identified by Reference <DataField>{data.dealReference}</DataField> (the "
        <strong>Deal</strong>").
      </li>
      <li>
        The Potential Buyer acknowledges that the identity of the Seller is{" "}
        <strong>confidential</strong> and will only be disclosed in writing by AcquizGroup after
        execution of this Agreement.
      </li>
      <li>
        The Potential Buyer acknowledges and agrees that{" "}
        <strong>AcquizGroup acts solely as a technology platform and liaison agent</strong> to
        facilitate the transmission of Confidential Information on behalf of the Seller, without
        acting as a broker or advisor in the transaction.
      </li>
    </ol>

    <SectionHeader>1. Definitions</SectionHeader>
    <p>
      <strong>1.1. Confidential Information.</strong> Means all non-public, commercial, financial
      (including revenue, adjusted EBITDA/SDE), operational, technical, legal, or other information
      relating to the Deal, disclosed whether orally, in writing, visually, or electronically, by
      the Disclosing Parties to the Potential Buyer.
    </p>
    <p>
      <strong>1.2. Deal.</strong> Means the commercial acquisition opportunity identified by
      Reference <DataField>{data.dealReference}</DataField>, whose operational details and owner
      identity are maintained confidential until express disclosure by AcquizGroup.
    </p>
    <p>
      <strong>1.3. Seller.</strong> Means the current owner of the business that is the subject of
      the Deal, whose identity remains confidential and will be communicated only after signature of
      this Agreement.
    </p>

    <SectionHeader>2. Confidentiality Obligations and Duration</SectionHeader>
    <p>
      <strong>2.1. Confidentiality Obligation.</strong> The Potential Buyer agrees to maintain
      strict confidentiality of the Confidential Information and to use it solely for the purpose of
      evaluating the Deal.
    </p>
    <p>
      <strong>2.2. Use Restrictions.</strong> The Potential Buyer agrees to:
    </p>
    <ul>
      <li>
        Not disclose Confidential Information to third parties without prior written consent of the
        Disclosing Parties;
      </li>
      <li>
        Limit access to Confidential Information only to employees, advisors, or representatives who
        have a legitimate need to know and who are bound by equivalent confidentiality obligations;
      </li>
      <li>Not use Confidential Information for purposes other than evaluating the Deal;</li>
      <li>
        Take all reasonable measures to protect the confidentiality of the Confidential Information.
      </li>
    </ul>
    <p>
      <strong>2.3. Duration.</strong> The confidentiality obligations shall remain in effect for a
      period of{" "}
      <strong>
        <DataField>{data.confidentialityYears}</DataField>
      </strong>{" "}
      years from the Effective Date.
    </p>

    <SectionHeader>3. Exclusions (Exceptions to Confidentiality)</SectionHeader>
    <p>The confidentiality obligations do not apply to information that:</p>
    <ol type="a">
      <li>Is or becomes publicly available without fault of the Potential Buyer;</li>
      <li>
        Was lawfully known to the Potential Buyer without confidentiality obligation prior to the
        Effective Date;
      </li>
      <li>
        Is received from a third party without disclosure restrictions and without breach of a
        confidentiality obligation;
      </li>
      <li>
        Is independently developed by the Potential Buyer without use of the Confidential
        Information;
      </li>
      <li>
        Must be disclosed pursuant to law, regulation, or court order, subject to prior notification
        to the Disclosing Parties to the extent possible.
      </li>
    </ol>

    <SectionHeader>4. Non-Solicitation</SectionHeader>
    <p>
      For a period of{" "}
      <strong>
        <DataField>{data.nonSolicitationYears}</DataField>
      </strong>{" "}
      years from the Effective Date, the Potential Buyer shall refrain from soliciting or engaging,
      directly or indirectly, any employee, officer, supplier, or customer of the Deal, without
      prior written consent of the Seller or Broker.
    </p>
    <p>
      <strong>4.1. Scope of Non-Solicitation.</strong> This clause applies to any attempt to:
    </p>
    <ul>
      <li>Recruit or hire key employees of the business;</li>
      <li>Divert customers or suppliers from the Deal;</li>
      <li>Induce business partners to terminate their relationships with the Deal.</li>
    </ul>
    <p>
      <strong>4.2. Exception.</strong> This restriction does not apply to general, non-targeted
      employment solicitations (public advertisements, recruitment agencies) where the employee
      responds on their own initiative.
    </p>

    <SectionHeader>5. General Provisions</SectionHeader>
    <p>
      <strong>5.1. Governing Law.</strong> This Agreement shall be governed by and construed in
      accordance with the laws of{" "}
      <strong>
        <DataField>{data.governingLaw}</DataField>
      </strong>
      .
    </p>
    <p>
      <strong>5.2. Return of Information.</strong> Upon written request from the Disclosing Parties,
      the Potential Buyer shall promptly return or destroy all copies of Confidential Information in
      its possession, and provide written certification of such destruction.
    </p>
    <p>
      <strong>5.3. No Warranty.</strong> The Disclosing Parties make no representation or warranty,
      express or implied, as to the accuracy or completeness of the Confidential Information. The
      Potential Buyer assumes full responsibility for its own analysis and due diligence.
    </p>
    <p>
      <strong>5.4. No Obligation to Transact.</strong> This Agreement creates no obligation for any
      Party to complete a transaction. Any potential transaction will be subject to negotiation and
      execution of separate definitive agreements.
    </p>
    <p>
      <strong>5.5. Remedies.</strong> The Potential Buyer acknowledges that a breach of this
      Agreement could cause irreparable harm to the Disclosing Parties and the Seller, and that
      monetary damages may be insufficient. Accordingly, the Disclosing Parties shall have the right
      to seek injunctive relief or other equitable remedies, in addition to all other remedies
      available at law.
    </p>
    <p>
      <strong>5.6. Severability.</strong> If any provision of this Agreement is found to be invalid
      or unenforceable, the remaining provisions shall remain in full force and effect to the
      fullest extent permitted by law.
    </p>
    <p>
      <strong>5.7. Entire Agreement.</strong> This Agreement constitutes the entire agreement
      between the Parties concerning its subject matter and supersedes all prior agreements,
      discussions, or understandings, written or oral.
    </p>

    <SectionHeader>6. Signatures and Commitment</SectionHeader>

    <p style={{ marginTop: "20px" }}>
      <strong>IN WITNESS WHEREOF</strong>, the Parties have executed this Agreement as of the date
      indicated below.
    </p>

    <table style={{ width: "100%", marginTop: "30px", borderCollapse: "collapse" }}>
      <tbody>
        <tr>
          <td style={{ width: "45%", verticalAlign: "top" }}>
            <div style={{ borderTop: "2px solid #333", paddingTop: "10px", marginTop: "50px" }}>
              <p style={{ fontWeight: "bold" }}>DISCLOSING PARTY I (ACQUIZGROUP)</p>
              <SignatureBox>
                <p>
                  <strong>Signature:</strong> <DataField>AcquizGroup</DataField>
                </p>
                <p>
                  <strong>Title:</strong> <DataField>Authorized Liaison Agent</DataField>
                </p>
                <p>
                  <strong>Date:</strong> <DataField>{data.generationDate}</DataField>
                </p>
              </SignatureBox>
            </div>
          </td>
          <td style={{ width: "10%" }}></td>
          <td style={{ width: "45%", verticalAlign: "top" }}>
            <div style={{ borderTop: "2px solid #333", paddingTop: "10px", marginTop: "50px" }}>
              <p style={{ fontWeight: "bold" }}>RECEIVING PARTY ({data.recipientType === "lender" ? "LENDER" : "POTENTIAL BUYER"})</p>
              <SignatureBox>
                <p>
                  <strong>Signature:</strong>{" "}
                  {data.recipientSignature ? (
                    <img src={data.recipientSignature} alt="Signature" style={{ maxWidth: "200px", maxHeight: "60px" }} />
                  ) : (
                    "[Awaiting signature]"
                  )}
                </p>
                <p>
                  <strong>Name:</strong> <DataField>{data.recipientName}</DataField>
                </p>
                <p>
                  <strong>Date:</strong> <DataField>{data.generationDate}</DataField>
                </p>
              </SignatureBox>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    {data.hasBroker && (
      <div style={{ marginTop: "40px", borderTop: "1px solid #ddd", paddingTop: "20px" }}>
        <p style={{ fontWeight: "bold" }}>DISCLOSING PARTY II (THE BROKER)</p>
        <div style={{ width: "45%" }}>
          <SignatureBox>
            <p>
              <strong>Signature:</strong> [Awaiting signature]
            </p>
            <p>
              <strong>Name:</strong> <DataField>{data.brokerName}</DataField>
            </p>
            <p>
              <strong>Date:</strong> [Pending]
            </p>
          </SignatureBox>
        </div>
      </div>
    )}

    <div
      style={{
        marginTop: "40px",
        backgroundColor: "#f0f0f0",
        padding: "15px",
        borderRadius: "5px",
      }}
    >
      <p style={{ fontSize: "14px", margin: 0 }}>
        <strong>🔐 Security and Compliance</strong>
      </p>
      <p style={{ fontSize: "12px", margin: "5px 0 0 0" }}>
        This document is automatically generated by the AcquizGroup platform. All electronic
        signatures are legally valid in accordance with applicable laws.
      </p>
    </div>
  </div>
);

// Composant inline pour les champs éditables dans le document (vue client)
const EditableField: React.FC<{
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  type?: string;
}> = ({ value, onChange, placeholder, type = "text" }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{
      fontWeight: "bold",
      color: "#007bff",
      backgroundColor: "#e7f3ff",
      padding: "4px 8px",
      borderRadius: "3px",
      border: "1px solid #b3d7ff",
      fontSize: "inherit",
      fontFamily: "inherit",
      minWidth: "180px",
    }}
  />
);

// Page principale du document NDA
const NDADocument: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState<"fr" | "en">("en");
  const [data, setData] = useState<NDAData>(defaultDataEN);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [saving, setSaving] = useState(false);
  const appId = import.meta.env.VITE_APP_ID || 'default-app-id';

  // Detect admin/staff role
  const isAdminOrStaff = user?.roles?.includes("admin") || user?.roles?.includes("staff");
  const [showForm, setShowForm] = useState(false); // Will be set after role check

  // Get URL params for auto-fill from listing details
  const prefilledDealId = searchParams.get('deal');
  const prefilledDealName = searchParams.get('dealName');
  const prefilledIndustry = searchParams.get('industry');
  const recipientTypeParam = searchParams.get('recipientType') as 'buyer' | 'lender' | null;

  const getStaticListingMeta = (dealKey: string) => {
    const listingDetail = getListingBySlug(dealKey);
    if (!listingDetail) return null;

    const listing = MOCK_LISTINGS.find((item) => item.id === listingDetail.id);
    if (!listing) return null;

    return {
      listingNumber: listing.listingNumber,
      industryCode: `DEAL-${(listing.category || dealKey).slice(0, 8).toUpperCase()}`,
    };
  };

  // Set showForm based on role
  useEffect(() => {
    setShowForm(!!isAdminOrStaff);
  }, [isAdminOrStaff]);

  // Fetch deals from Firestore for auto-fill
  useEffect(() => {
    const fetchDeals = async () => {
      setLoadingDeals(true);
      try {
        const snap = await getDocs(query(collection(db, "deals"), orderBy("createdAt", "desc")));
const dealsData = snap.docs.map(d => ({ id: d.id, business_name: d.data().businessName || "", business_description: d.data().businessDescription || "", broker_id: d.data().brokerId || null, seller_id: d.data().sellerId || null, reference_number: d.data().referenceNumber || null }));
setDeals(dealsData);

        // Auto-select deal from URL params
        if (prefilledDealId && dealsData) {
          const deal = dealsData.find(d => d.id === prefilledDealId);
          if (deal) {
            handleDealSelectWithBrokerFetch(deal);
          } else if (prefilledDealName) {
            const staticListingMeta = getStaticListingMeta(prefilledDealId);
            setData(prev => ({
              ...prev,
              dealId: prefilledDealId,
              dealReference: staticListingMeta?.listingNumber || `DEAL-${prefilledDealId.slice(0, 8).toUpperCase()}`,
              businessName: prefilledDealName,
              industrySector: prefilledIndustry || staticListingMeta?.industryCode || '',
              recipientType: recipientTypeParam || 'buyer',
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching deals:", error);
      } finally {
        setLoadingDeals(false);
      }
    };

    fetchDeals();
  }, [prefilledDealId, prefilledDealName, prefilledIndustry, recipientTypeParam]);

  // Auto-fill recipient info from logged-in user + Firestore registration data
  useEffect(() => {
    if (user) {
      setData((prev) => ({
        ...prev,
        recipientName: user.displayName || user.email?.split("@")[0] || "",
        recipientEmail: user.email || "",
        recipientType: recipientTypeParam || 'buyer',
      }));

      const fetchUserProfile = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.id));
          if (userDoc.exists()) {
            const profile = userDoc.data();
            setData((prev) => ({
              ...prev,
              recipientPhone: prev.recipientPhone || profile.phone || profile.telephone || "",
              recipientAddress: prev.recipientAddress || (
                profile.address
                  ? [profile.address.street, profile.address.city, profile.address.province, profile.address.postalCode, profile.address.country].filter(Boolean).join(', ')
                  : ""
              ),
            }));
          }
        } catch (err) {
          console.error('Error fetching user profile for NDA:', err);
        }
      };
      fetchUserProfile();
    }
  }, [user, recipientTypeParam]);

  // Update default data when language changes
  useEffect(() => {
    setData((prev) => ({
      ...prev,
      effectiveDate: language === "fr" 
        ? new Date().toLocaleDateString('fr-CA') 
        : new Date().toLocaleDateString('en-US'),
      generationDate: language === "fr" 
        ? new Date().toLocaleDateString('fr-CA') 
        : new Date().toLocaleDateString('en-US'),
      governingLaw: language === "fr" 
        ? "l'État de Floride, États-Unis" 
        : "the State of Florida, United States",
    }));
  }, [language]);

  // Handle deal selection with broker info fetch
  const handleDealSelectWithBrokerFetch = async (selectedDeal: Deal) => {
    let brokerInfo = { name: '', address: '', email: '' };
    let sellerInfo = { id: selectedDeal.seller_id || '', name: '', email: '' };

    if (selectedDeal.broker_id) {
      try {
        const brokerDoc = await getDoc(doc(db, "users", selectedDeal.broker_id));
const profile = brokerDoc.exists() ? { display_name: brokerDoc.data().displayName, email: brokerDoc.data().email } : null;
        
        if (profile) {
          brokerInfo = {
            name: profile.display_name || '',
            address: '',
            email: profile.email || '',
          };
        }
      } catch (err) {
        console.error('Error fetching broker info:', err);
      }
    }

    if (selectedDeal.seller_id) {
      try {
        const sellerSnap = await getDoc(doc(db, 'userProfiles', selectedDeal.seller_id));
        const profile: any = sellerSnap.exists() ? sellerSnap.data() : null;
        if (profile) {
          sellerInfo = {
            id: selectedDeal.seller_id,
            name: profile.displayName || profile.display_name || '',
            email: profile.email || '',
          };
        }
      } catch (err) {
        console.error('Error fetching seller info:', err);
      }
    }

    setData((prev) => ({
      ...prev,
      dealId: selectedDeal.id,
      dealReference: selectedDeal.reference_number || `DEAL-${selectedDeal.id.slice(0, 8).toUpperCase()}`,
      businessName: selectedDeal.business_name,
      industrySector: selectedDeal.business_description?.split(" ").slice(0, 3).join(" ") || "",
      hasBroker: !!selectedDeal.broker_id,
      brokerName: brokerInfo.name,
      brokerAddress: brokerInfo.address,
      brokerEmail: brokerInfo.email,
      sellerId: sellerInfo.id,
      sellerName: sellerInfo.name,
      sellerEmail: sellerInfo.email,
    }));
  };

  const handleDealSelect = (dealId: string) => {
    const selectedDeal = deals.find((d) => d.id === dealId);
    if (selectedDeal) {
      handleDealSelectWithBrokerFetch(selectedDeal);
    } else {
      setData((prev) => ({
        ...prev,
        dealId: "",
        dealReference: "",
        industrySector: "",
        hasBroker: false,
        brokerName: "",
        brokerAddress: "",
        brokerEmail: "",
      }));
    }
  };

  const handleSignatureChange = (signature: string | undefined) => {
    setData((prev) => ({ ...prev, recipientSignature: signature }));
  };

  const sendSignatureNotification = async (documentId: string) => {
    const signers = [
      {
        email: data.recipientEmail,
        name: data.recipientName,
        role: data.recipientType === 'lender' ? 'Lender' : 'Buyer',
      },
    ];

    if (data.sellerEmail && data.sellerName) {
      signers.push({
        email: data.sellerEmail,
        name: data.sellerName,
        role: 'Seller',
      });
    }

    if (data.hasBroker && data.brokerEmail && data.brokerName) {
      signers.push({
        email: data.brokerEmail,
        name: data.brokerName,
        role: 'Broker',
      });
    }

    try {
      await invokeFunctionSilent('send-signature-complete-notification', { document_id: documentId, document_type: 'nda', deal_id: data.dealId, deal_name: data.businessName || data.dealReference, signers });
    } catch (err) {
      console.error('Error invoking notification function:', err);
    }
  };

  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  const handleSaveNDA = async () => {
    if (!data.dealId || !data.recipientSignature || !user) return;

    setSaving(true);
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) throw new Error("Not authenticated");

      const idToken = await firebaseUser.getIdToken();
      const sourceDealKey = prefilledDealId || data.dealId;

      const saveResult = await invokeFunction("save-signed-nda", { dealId: sourceDealKey, dealReference: data.dealReference, businessName: data.businessName, industrySector: data.industrySector, sellerId: data.sellerId, recipientType: data.recipientType, ndaData: data });

      if (!saveResult?.success) {
        throw new Error(saveResult?.error || "Unable to save signed NDA");
      }

      const signedDocument = {
        type: 'NDA',
        dealId: sourceDealKey,
        receivingUid: user.id,
        disclosingCompanyName: data.adminPartyName,
        purpose: data.businessName || data.dealReference,
        confidentialInfoDefinition: data.industrySector,
        termMonths: data.confidentialityYears * 12,
        nonDisclosurePeriod: data.confidentialityYears,
        applicableLaw: data.governingLaw,
        enforcement: 'signed',
        signatures: { receiving: data.recipientSignature },
        status: 'signed',
        signedAt: new Date().toISOString(),
      };

      await addDoc(collection(db, `artifacts/${appId}/users/${user.id}/user_documents`), {
        ...signedDocument,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      try {
        const ndaRef = collection(db, 'ndaRequests');
        const listingDetail = sourceDealKey ? getListingBySlug(sourceDealKey) : undefined;
        const requestDealId = listingDetail?.id || sourceDealKey;
        const existingRequestQuery = query(
          ndaRef,
          where('buyerId', '==', user.id),
          where('dealId', '==', requestDealId),
          limit(1),
        );
        const existingRequestSnap = await getDocs(existingRequestQuery);

        if (!existingRequestSnap.empty) {
          await updateDoc(existingRequestSnap.docs[0].ref, {
            status: 'signed',
            signedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } else {
          // Create ndaRequests doc so dashboard and listing page can detect the signed NDA
          const content = listingDetail?.[language as 'en' | 'fr'] || listingDetail?.en;
          await addDoc(ndaRef, {
            buyerId: user.id,
            dealId: requestDealId,
            dealSlug: sourceDealKey,
            dealName: content?.title || data.businessName || sourceDealKey,
            sellerId: data.sellerId || 'AcquizGroup',
            status: 'signed',
            signedAt: serverTimestamp(),
            requestedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            documentUrl: `/tools/nda-generator?deal=${sourceDealKey}`,
          });
        }
      } catch (syncError) {
        console.warn('Unable to sync ndaRequests status:', syncError);
      }

      if (saveResult.documentId) {
        await sendSignatureNotification(saveResult.documentId);
      }

      try {
        const documentEl = document.querySelector('[data-nda-document]');
        if (documentEl) {
          await generatePdfFromHtml(documentEl.outerHTML, {
            filename: `NDA-${data.dealReference}-${data.recipientName.replace(/\s+/g, "_")}.pdf`,
          });
        }
      } catch (pdfError) {
        console.warn('NDA saved but PDF generation failed:', pdfError);
      }

      alert(language === "fr" ? "NDA signé et sauvegardé avec succès!" : "NDA signed and saved successfully!");
      
      if (data.recipientType === 'lender') {
        navigate('/dashboard/lender');
      } else if (prefilledDealId) {
        navigate(`/listings/${prefilledDealId}`);
      }
    } catch (error) {
      console.error("Error saving NDA:", error);
      alert(language === "fr" ? "Erreur lors de la sauvegarde du NDA" : "Error saving NDA");
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = data.recipientName && data.recipientEmail && data.dealId;

  // Recipient type label helper
  const recipientLabel = (lang: "fr" | "en", type: "buyer" | "lender") => {
    if (lang === "fr") return type === "lender" ? "Le Prêteur" : "L'Acheteur Potentiel";
    return type === "lender" ? "The Lender" : "The Potential Buyer";
  };

  const recipientLabelShort = (lang: "fr" | "en", type: "buyer" | "lender") => {
    if (lang === "fr") return type === "lender" ? "le « Prêteur »" : "l'« Acheteur Potentiel »";
    return type === "lender" ? "the \"Lender\"" : "the \"Potential Buyer\"";
  };

  const recipientLabelUpper = (lang: "fr" | "en", type: "buyer" | "lender") => {
    if (lang === "fr") return type === "lender" ? "PRÊTEUR" : "ACHETEUR POTENTIEL";
    return type === "lender" ? "LENDER" : "POTENTIAL BUYER";
  };

  // Generic term used in the body of the NDA to avoid buyer/lender confusion
  const recipientTermBody = (lang: "fr" | "en", type: "buyer" | "lender") => {
    if (lang === "fr") return type === "lender" ? "Le Prêteur" : "L'Acheteur Potentiel";
    return type === "lender" ? "The Lender" : "The Potential Buyer";
  };

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {/* Barre de contrôle en haut */}
      <div
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: "#1D314D",
          borderBottom: "1px solid #dee2e6",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 100,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <img src="/noBgColor.png" alt="AcquizGroup" style={{ height: "32px" }} />
          <span style={{ color: "#fff", fontWeight: "bold", fontSize: "18px" }}>
            {language === "fr" ? "Accord de Non-Divulgation" : "Non-Disclosure Agreement"}
          </span>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => setLanguage("fr")}
            style={{
              padding: "8px 16px",
              backgroundColor: language === "fr" ? "#CEAB65" : "transparent",
              color: "#fff",
              border: "1px solid #CEAB65",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: language === "fr" ? "bold" : "normal",
            }}
          >
            FR
          </button>
          <button
            onClick={() => setLanguage("en")}
            style={{
              padding: "8px 16px",
              backgroundColor: language === "en" ? "#CEAB65" : "transparent",
              color: "#fff",
              border: "1px solid #CEAB65",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: language === "en" ? "bold" : "normal",
            }}
          >
            EN
          </button>
          {isAdminOrStaff && (
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: "8px 16px",
                backgroundColor: "#2E4663",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "20px",
              }}
            >
              {showForm 
                ? (language === "fr" ? "Masquer le formulaire" : "Hide Form") 
                : (language === "fr" ? "Afficher le formulaire" : "Show Form")}
            </button>
          )}
          {isAdminOrStaff && (
            <button
              onClick={() => {
                const target = user?.roles?.includes("admin") ? "/admin/overview" : "/staff";
                navigate(target);
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: "transparent",
                color: "#fff",
                border: "1px solid #CEAB65",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "8px",
              }}
              title={language === "fr" ? "Retour au tableau de bord" : "Back to dashboard"}
            >
              ← {language === "fr" ? "Retour" : "Back"}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex" }}>
        {/* Formulaire admin uniquement pour admin/staff */}
        {showForm && isAdminOrStaff && (
          <div
            style={{
              width: "420px",
              minWidth: "420px",
              borderRight: "1px solid #dee2e6",
              maxHeight: "calc(100vh - 60px)",
              overflowY: "scroll",
              scrollbarWidth: "auto",
              backgroundColor: "#fff",
              boxShadow: "inset -4px 0 6px -4px rgba(0,0,0,0.08)",
            }}
          >
            {/* Sticky action bar: Save / Load draft */}
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 5,
                background: "#1D314D",
                color: "#fff",
                padding: "10px 16px",
                display: "flex",
                gap: "8px",
                alignItems: "center",
                borderBottom: "2px solid #CEAB65",
              }}
            >
              <span style={{ fontSize: "12px", fontWeight: 600, marginRight: "auto" }}>
                {language === "fr" ? "Brouillon (admin/staff)" : "Draft (admin/staff)"}
              </span>
              <button
                type="button"
                onClick={() => {
                  try {
                    const key = `nda_draft_${data.dealId || data.dealReference || "unsaved"}`;
                    localStorage.setItem(key, JSON.stringify({ data, savedAt: new Date().toISOString() }));
                    localStorage.setItem("nda_draft_last", key);
                    alert(language === "fr" ? "Brouillon sauvegardé" : "Draft saved");
                  } catch (e) {
                    alert(language === "fr" ? "Échec de la sauvegarde" : "Save failed");
                  }
                }}
                style={{
                  padding: "6px 12px",
                  background: "#CEAB65",
                  color: "#1D314D",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                💾 {language === "fr" ? "Sauvegarder brouillon" : "Save draft"}
              </button>
              <button
                type="button"
                onClick={() => {
                  try {
                    const key = `nda_draft_${data.dealId || data.dealReference || "unsaved"}`;
                    const raw = localStorage.getItem(key) || (localStorage.getItem("nda_draft_last") && localStorage.getItem(localStorage.getItem("nda_draft_last")!));
                    if (!raw) {
                      alert(language === "fr" ? "Aucun brouillon trouvé" : "No draft found");
                      return;
                    }
                    const parsed = JSON.parse(raw);
                    setData(parsed.data);
                    alert(language === "fr" ? "Brouillon chargé" : "Draft loaded");
                  } catch {
                    alert(language === "fr" ? "Échec du chargement" : "Load failed");
                  }
                }}
                style={{
                  padding: "6px 12px",
                  background: "transparent",
                  color: "#fff",
                  border: "1px solid #CEAB65",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                📂 {language === "fr" ? "Charger" : "Load"}
              </button>
            </div>
            <div style={{ padding: "20px", paddingBottom: "40px" }}>
              <DataInputForm 
                data={data} 
                setData={setData} 
                language={language}
                deals={deals}
                loadingDeals={loadingDeals}
                onDealSelect={handleDealSelect}
              />

              {/* Admin/staff signature inside the scrollable form column — never covers the questionnaire */}
              <div style={{ marginTop: "24px", padding: "16px", border: "2px solid #CEAB65", borderRadius: "8px", backgroundColor: "#fff" }}>
                <h4 style={{ marginTop: 0, marginBottom: "12px", color: "#1D314D", fontSize: "14px", fontWeight: 700 }}>
                  {language === "fr" ? "Signature AcquizGroup (admin/staff)" : "AcquizGroup Signature (admin/staff)"}
                </h4>
                <SignaturePadComponent onSignatureChange={handleSignatureChange} language={language} />
                <button
                  disabled={!isFormValid || !data.recipientSignature || saving}
                  onClick={handleSaveNDA}
                  style={{
                    width: "100%",
                    marginTop: "12px",
                    padding: "12px",
                    backgroundColor: isFormValid && data.recipientSignature && !saving ? "#28a745" : "#ccc",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isFormValid && data.recipientSignature && !saving ? "pointer" : "not-allowed",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {saving 
                    ? (language === "fr" ? "Sauvegarde..." : "Saving...") 
                    : (language === "fr" ? "Signer et Sauvegarder" : "Sign and Save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document NDA + inline editable fields for clients */}
        <div style={{ flex: 1, maxHeight: "calc(100vh - 60px)", overflowY: "auto" }} data-nda-document>
          {/* The NDA document - using inline editable Part B for clients */}
          <div
            style={{
              fontFamily: "Arial, sans-serif",
              lineHeight: 1.6,
              padding: "40px",
              maxWidth: "900px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                textAlign: "center",
                marginBottom: "30px",
              }}
            >
              {language === "fr" 
                ? "ACCORD DE NON-DIVULGATION, DE CONFIDENTIALITÉ ET DE NON-SOLLICITATION MAÎTRE"
                : "MASTER NON-DISCLOSURE, CONFIDENTIALITY AND NON-SOLICITATION AGREEMENT"}
            </div>

            <p style={{ textAlign: "right", fontStyle: "italic" }}>
              <strong>{language === "fr" ? "Référence de l'Affaire :" : "Deal Reference:"}</strong>{" "}
              <DataField>{data.dealReference}</DataField>
              <br />
              <strong>{language === "fr" ? "Secteur d'Activité :" : "Industry Sector:"}</strong>{" "}
              <DataField>{data.industrySector}</DataField>
            </p>

            <p>
              {language === "fr" 
                ? <>Le présent Accord est conclu et prend effet à compter du <strong><DataField>{data.effectiveDate}</DataField></strong> (la « <strong>Date Effective</strong> ») par et entre les Parties suivantes :</>
                : <>This Agreement is entered into and effective as of <strong><DataField>{data.effectiveDate}</DataField></strong> (the "<strong>Effective Date</strong>") by and between the following Parties:</>
              }
            </p>

            <SectionHeader>
              {language === "fr" 
                ? "A. Les Parties Divulguantes (Vendeur Anonyme / Agent de Liaison)"
                : "A. The Disclosing Parties (Anonymous Seller / Liaison Agent)"}
            </SectionHeader>

            <PartyBox>
              <p>
                <strong>{language === "fr" ? "Partie Divulguante I (L'Administrateur) :" : "Disclosing Party I (The Administrator):"}</strong>
              </p>
              <ul>
                <li>
                  <strong>{language === "fr" ? "Nom" : "Name"}</strong> : <DataField>{data.adminPartyName}</DataField>{" "}
                  {language === "fr" 
                    ? "(AcquizGroup, agissant en tant qu'Administrateur de la Plateforme et agent de liaison autorisé par le Vendeur)"
                    : "(AcquizGroup, acting as Platform Administrator and authorized liaison agent for the Seller)"}
                </li>
              </ul>
              {data.hasBroker && (
                <>
                  <p style={{ marginTop: "10px" }}>
                    <strong>{language === "fr" ? "Partie Divulguante II (Le Broker, si applicable) :" : "Disclosing Party II (The Broker, if applicable):"}</strong>
                  </p>
                  <ul>
                    <li><strong>{language === "fr" ? "Nom" : "Name"}</strong> : <DataField>{data.brokerName}</DataField></li>
                    <li><strong>{language === "fr" ? "Adresse" : "Address"}</strong> : <DataField>{data.brokerAddress}</DataField></li>
                  </ul>
                </>
              )}
              <p>
                {language === "fr" 
                  ? "(Collectivement les « Parties Divulguantes »)"
                  : "(Collectively the \"Disclosing Parties\")"}
              </p>
            </PartyBox>

            <SectionHeader>
              {language === "fr" 
                ? `B. La Partie Récipiendaire (${recipientLabel("fr", data.recipientType)})`
                : `B. The Receiving Party (${recipientLabel("en", data.recipientType)})`}
            </SectionHeader>

            <PartyBox>
              {/* For clients: inline editable fields */}
              {!isAdminOrStaff ? (
                <div>
                  <div style={{ marginBottom: "12px" }}>
                    <strong>{language === "fr" ? "Nom / Name" : "Name"} :</strong>{" "}
                    <EditableField
                      value={data.recipientName}
                      onChange={(val) => setData(prev => ({ ...prev, recipientName: val }))}
                      placeholder={language === "fr" ? "Votre nom complet" : "Your full name"}
                    />
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <strong>{language === "fr" ? "Adresse / Address" : "Address"} :</strong>{" "}
                    <EditableField
                      value={data.recipientAddress}
                      onChange={(val) => setData(prev => ({ ...prev, recipientAddress: val }))}
                      placeholder={language === "fr" ? "Votre adresse" : "Your address"}
                    />
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <strong>{language === "fr" ? "Courriel / Email" : "Email"} :</strong>{" "}
                    <EditableField
                      value={data.recipientEmail}
                      onChange={(val) => setData(prev => ({ ...prev, recipientEmail: val }))}
                      placeholder="email@example.com"
                      type="email"
                    />
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <strong>{language === "fr" ? "Téléphone / Phone" : "Phone"} :</strong>{" "}
                    <EditableField
                      value={data.recipientPhone}
                      onChange={(val) => setData(prev => ({ ...prev, recipientPhone: val }))}
                      placeholder="+1 (555) 000-0000"
                      type="tel"
                    />
                  </div>
                  <p style={{ fontSize: "13px", color: "#666", fontStyle: "italic", marginTop: "8px" }}>
                    {language === "fr" 
                      ? `(ci-après ${recipientLabelShort("fr", data.recipientType)})`
                      : `(hereinafter ${recipientLabelShort("en", data.recipientType)})`}
                  </p>
                </div>
              ) : (
                <ul>
                  <li><strong>{language === "fr" ? "Nom" : "Name"}</strong> : <strong><DataField>{data.recipientName}</DataField></strong>{" "}
                    {language === "fr" 
                      ? `(ci-après ${recipientLabelShort("fr", data.recipientType)})`
                      : `(hereinafter ${recipientLabelShort("en", data.recipientType)})`}
                  </li>
                  <li><strong>{language === "fr" ? "Adresse" : "Address"}</strong> : <DataField>{data.recipientAddress}</DataField></li>
                  <li><strong>{language === "fr" ? "E-mail" : "Email"}</strong> : <DataField>{data.recipientEmail}</DataField></li>
                  <li><strong>{language === "fr" ? "Téléphone" : "Phone"}</strong> : <DataField>{data.recipientPhone}</DataField></li>
                </ul>
              )}
            </PartyBox>

            {/* Body clauses - use recipientTermBody for all references */}
            <SectionHeader highlight>
              {language === "fr" ? "CONSIDÉRATIONS PRÉLIMINAIRES (RÔLE ACQUIZGROUP)" : "PRELIMINARY RECITALS (ACQUIZGROUP ROLE)"}
            </SectionHeader>
            <ol>
              <li>
                {language === "fr" 
                  ? <>{recipientTermBody("fr", data.recipientType)} désire évaluer une opportunité d'acquisition de l'entreprise identifiée par la Référence <DataField>{data.dealReference}</DataField> (l'« <strong>Affaire</strong> »).</>
                  : <>{recipientTermBody("en", data.recipientType)} wishes to evaluate an acquisition opportunity for the business identified by Reference <DataField>{data.dealReference}</DataField> (the "<strong>Deal</strong>").</>
                }
              </li>
              <li>
                {language === "fr" 
                  ? <>{recipientTermBody("fr", data.recipientType)} reconnaît que l'identité du Vendeur est <strong>confidentielle</strong> et ne sera divulguée que par écrit par AcquizGroup après l'exécution du présent Accord.</>
                  : <>{recipientTermBody("en", data.recipientType)} acknowledges that the identity of the Seller is <strong>confidential</strong> and will only be disclosed in writing by AcquizGroup after execution of this Agreement.</>
                }
              </li>
              <li>
                {language === "fr" 
                  ? <>{recipientTermBody("fr", data.recipientType)} reconnaît et accepte qu'<strong>AcquizGroup agit uniquement en tant que plateforme technologique et agent de liaison</strong> pour faciliter la transmission des Informations Confidentielles pour le compte du Vendeur, sans agir comme courtier ou conseiller dans la transaction.</>
                  : <>{recipientTermBody("en", data.recipientType)} acknowledges and agrees that <strong>AcquizGroup acts solely as a technology platform and liaison agent</strong> to facilitate the transmission of Confidential Information on behalf of the Seller, without acting as a broker or advisor in the transaction.</>
                }
              </li>
            </ol>

            {language === "fr" ? (
              <>
                <SectionHeader>1. Définitions</SectionHeader>
                <p><strong>1.1. Informations Confidentielles.</strong> Désigne toutes les informations non publiques, commerciales, financières (incluant CA, EBITDA/SDE ajusté), opérationnelles, techniques, juridiques ou autres, relatives à l'Affaire, divulguées, que ce soit oralement, par écrit, visuellement ou électroniquement, par les Parties Divulguantes à {recipientTermBody("fr", data.recipientType)}.</p>
                <p><strong>1.2. Affaire.</strong> Désigne l'opportunité d'acquisition commerciale identifiée par la Référence <DataField>{data.dealReference}</DataField>, dont les détails opérationnels et l'identité du propriétaire sont maintenus confidentiels jusqu'à divulgation expresse par AcquizGroup.</p>
                <p><strong>1.3. Vendeur.</strong> Désigne le propriétaire actuel de l'entreprise faisant l'objet de l'Affaire, dont l'identité demeure confidentielle et sera communiquée uniquement après signature du présent Accord.</p>

                <SectionHeader>2. Obligations de Confidentialité et Durée</SectionHeader>
                <p><strong>2.1. Obligation de Confidentialité.</strong> {recipientTermBody("fr", data.recipientType)} s'engage à maintenir la stricte confidentialité des Informations Confidentielles et à ne les utiliser que dans le but exclusif d'évaluer l'Affaire.</p>
                <p><strong>2.2. Restrictions d'Utilisation.</strong> {recipientTermBody("fr", data.recipientType)} s'engage à :</p>
                <ul>
                  <li>Ne pas divulguer les Informations Confidentielles à des tiers sans le consentement écrit préalable des Parties Divulguantes ;</li>
                  <li>Limiter l'accès aux Informations Confidentielles uniquement aux employés, conseillers ou représentants qui ont un besoin légitime de les connaître et qui sont liés par des obligations de confidentialité équivalentes ;</li>
                  <li>Ne pas utiliser les Informations Confidentielles à des fins autres que l'évaluation de l'Affaire ;</li>
                  <li>Prendre toutes les mesures raisonnables pour protéger la confidentialité des Informations Confidentielles.</li>
                </ul>
                <p><strong>2.3. Durée.</strong> Les obligations de confidentialité resteront en vigueur pendant une période de <strong><DataField>{data.confidentialityYears}</DataField></strong> ans à compter de la Date Effective.</p>

                <SectionHeader>3. Exclusions (Exceptions à la Confidentialité)</SectionHeader>
                <p>Les obligations de confidentialité ne s'appliquent pas aux informations qui :</p>
                <ol type="a">
                  <li>Sont ou deviennent accessibles au public sans faute de {recipientTermBody("fr", data.recipientType)} ;</li>
                  <li>Étaient légalement connues de {recipientTermBody("fr", data.recipientType)} sans obligation de confidentialité avant la Date Effective ;</li>
                  <li>Sont reçues d'un tiers sans restriction de divulgation et sans violation d'une obligation de confidentialité ;</li>
                  <li>Sont développées de manière indépendante par {recipientTermBody("fr", data.recipientType)} sans utilisation des Informations Confidentielles ;</li>
                  <li>Doivent être divulguées en vertu d'une loi, d'un règlement ou d'une ordonnance judiciaire, sous réserve d'une notification préalable aux Parties Divulguantes dans la mesure du possible.</li>
                </ol>

                <SectionHeader>4. Non-Sollicitation</SectionHeader>
                <p>Pendant une période de <strong><DataField>{data.nonSolicitationYears}</DataField></strong> ans à compter de la Date Effective, {recipientTermBody("fr", data.recipientType)} s'interdit de solliciter ou d'engager, directement ou indirectement, tout employé, dirigeant, fournisseur ou client de l'Affaire, sans l'accord écrit préalable du Vendeur ou du Broker.</p>
                <p><strong>4.1. Portée de la Non-Sollicitation.</strong> Cette clause s'applique à toute tentative de :</p>
                <ul>
                  <li>Recruter ou embaucher des employés clés de l'entreprise ;</li>
                  <li>Détourner des clients ou fournisseurs de l'Affaire ;</li>
                  <li>Inciter des partenaires commerciaux à rompre leurs relations avec l'Affaire.</li>
                </ul>
                <p><strong>4.2. Exception.</strong> Cette restriction ne s'applique pas aux sollicitations générales d'emploi non ciblées (annonces publiques, agences de recrutement) où l'employé répond de sa propre initiative.</p>

                <SectionHeader>5. Dispositions Générales</SectionHeader>
                <p><strong>5.1. Droit Applicable.</strong> Le présent Accord sera régi et interprété conformément aux lois de <strong><DataField>{data.governingLaw}</DataField></strong>.</p>
                <p><strong>5.2. Retour des Informations.</strong> Sur demande écrite des Parties Divulguantes, {recipientTermBody("fr", data.recipientType)} devra rapidement retourner ou détruire toutes les copies des Informations Confidentielles en sa possession, et fournir une certification écrite de cette destruction.</p>
                <p><strong>5.3. Absence de Garantie.</strong> Les Parties Divulguantes ne font aucune déclaration ou garantie, expresse ou implicite, quant à l'exactitude ou l'exhaustivité des Informations Confidentielles. {recipientTermBody("fr", data.recipientType)} assume l'entière responsabilité de sa propre analyse et diligence raisonnable.</p>
                <p><strong>5.4. Aucune Obligation de Transaction.</strong> Le présent Accord ne crée aucune obligation pour l'une ou l'autre des Parties de conclure une transaction. Toute transaction éventuelle sera soumise à la négociation et à l'exécution d'accords définitifs distincts.</p>
                <p><strong>5.5. Recours.</strong> {recipientTermBody("fr", data.recipientType)} reconnaît qu'une violation du présent Accord pourrait causer un préjudice irréparable aux Parties Divulguantes et au Vendeur, et que des dommages-intérêts monétaires pourraient être insuffisants. En conséquence, les Parties Divulguantes auront le droit de demander une injonction ou d'autres recours en équité, en plus de tous autres recours disponibles en droit.</p>
                <p><strong>5.6. Divisibilité.</strong> Si une disposition du présent Accord est jugée invalide ou inapplicable, les autres dispositions demeureront en vigueur dans toute la mesure permise par la loi.</p>
                <p><strong>5.7. Accord Intégral.</strong> Le présent Accord constitue l'intégralité de l'entente entre les Parties concernant son objet et remplace tous accords, discussions ou ententes antérieurs, écrits ou oraux.</p>
              </>
            ) : (
              <>
                <SectionHeader>1. Definitions</SectionHeader>
                <p><strong>1.1. Confidential Information.</strong> Means all non-public, commercial, financial (including revenue, adjusted EBITDA/SDE), operational, technical, legal, or other information relating to the Deal, disclosed whether orally, in writing, visually, or electronically, by the Disclosing Parties to {recipientTermBody("en", data.recipientType)}.</p>
                <p><strong>1.2. Deal.</strong> Means the commercial acquisition opportunity identified by Reference <DataField>{data.dealReference}</DataField>, whose operational details and owner identity are maintained confidential until express disclosure by AcquizGroup.</p>
                <p><strong>1.3. Seller.</strong> Means the current owner of the business that is the subject of the Deal, whose identity remains confidential and will be communicated only after signature of this Agreement.</p>

                <SectionHeader>2. Confidentiality Obligations and Duration</SectionHeader>
                <p><strong>2.1. Confidentiality Obligation.</strong> {recipientTermBody("en", data.recipientType)} agrees to maintain strict confidentiality of the Confidential Information and to use it solely for the purpose of evaluating the Deal.</p>
                <p><strong>2.2. Use Restrictions.</strong> {recipientTermBody("en", data.recipientType)} agrees to:</p>
                <ul>
                  <li>Not disclose Confidential Information to third parties without prior written consent of the Disclosing Parties;</li>
                  <li>Limit access to Confidential Information only to employees, advisors, or representatives who have a legitimate need to know and who are bound by equivalent confidentiality obligations;</li>
                  <li>Not use Confidential Information for purposes other than evaluating the Deal;</li>
                  <li>Take all reasonable measures to protect the confidentiality of the Confidential Information.</li>
                </ul>
                <p><strong>2.3. Duration.</strong> The confidentiality obligations shall remain in effect for a period of <strong><DataField>{data.confidentialityYears}</DataField></strong> years from the Effective Date.</p>

                <SectionHeader>3. Exclusions (Exceptions to Confidentiality)</SectionHeader>
                <p>The confidentiality obligations do not apply to information that:</p>
                <ol type="a">
                  <li>Is or becomes publicly available without fault of {recipientTermBody("en", data.recipientType)};</li>
                  <li>Was lawfully known to {recipientTermBody("en", data.recipientType)} without confidentiality obligation prior to the Effective Date;</li>
                  <li>Is received from a third party without disclosure restrictions and without breach of a confidentiality obligation;</li>
                  <li>Is independently developed by {recipientTermBody("en", data.recipientType)} without use of the Confidential Information;</li>
                  <li>Must be disclosed pursuant to law, regulation, or court order, subject to prior notification to the Disclosing Parties to the extent possible.</li>
                </ol>

                <SectionHeader>4. Non-Solicitation</SectionHeader>
                <p>For a period of <strong><DataField>{data.nonSolicitationYears}</DataField></strong> years from the Effective Date, {recipientTermBody("en", data.recipientType)} shall refrain from soliciting or engaging, directly or indirectly, any employee, officer, supplier, or customer of the Deal, without prior written consent of the Seller or Broker.</p>
                <p><strong>4.1. Scope of Non-Solicitation.</strong> This clause applies to any attempt to:</p>
                <ul>
                  <li>Recruit or hire key employees of the business;</li>
                  <li>Divert customers or suppliers from the Deal;</li>
                  <li>Induce business partners to terminate their relationships with the Deal.</li>
                </ul>
                <p><strong>4.2. Exception.</strong> This restriction does not apply to general, non-targeted employment solicitations (public advertisements, recruitment agencies) where the employee responds on their own initiative.</p>

                <SectionHeader>5. General Provisions</SectionHeader>
                <p><strong>5.1. Governing Law.</strong> This Agreement shall be governed by and construed in accordance with the laws of <strong><DataField>{data.governingLaw}</DataField></strong>.</p>
                <p><strong>5.2. Return of Information.</strong> Upon written request from the Disclosing Parties, {recipientTermBody("en", data.recipientType)} shall promptly return or destroy all copies of Confidential Information in its possession, and provide written certification of such destruction.</p>
                <p><strong>5.3. No Warranty.</strong> The Disclosing Parties make no representation or warranty, express or implied, as to the accuracy or completeness of the Confidential Information. {recipientTermBody("en", data.recipientType)} assumes full responsibility for its own analysis and due diligence.</p>
                <p><strong>5.4. No Obligation to Transact.</strong> This Agreement creates no obligation for any Party to complete a transaction. Any potential transaction will be subject to negotiation and execution of separate definitive agreements.</p>
                <p><strong>5.5. Remedies.</strong> {recipientTermBody("en", data.recipientType)} acknowledges that a breach of this Agreement could cause irreparable harm to the Disclosing Parties and the Seller, and that monetary damages may be insufficient. Accordingly, the Disclosing Parties shall have the right to seek injunctive relief or other equitable remedies, in addition to all other remedies available at law.</p>
                <p><strong>5.6. Severability.</strong> If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect to the fullest extent permitted by law.</p>
                <p><strong>5.7. Entire Agreement.</strong> This Agreement constitutes the entire agreement between the Parties concerning its subject matter and supersedes all prior agreements, discussions, or understandings, written or oral.</p>
              </>
            )}

            {/* Signature Section */}
            <SectionHeader>
              {language === "fr" ? "6. Signatures et Engagement" : "6. Signatures and Commitment"}
            </SectionHeader>

            <p style={{ marginTop: "20px" }}>
              <strong>{language === "fr" ? "EN FOI DE QUOI" : "IN WITNESS WHEREOF"}</strong>
              {language === "fr" 
                ? ", les Parties ont signé le présent Accord à la date indiquée ci-dessous."
                : ", the Parties have executed this Agreement as of the date indicated below."}
            </p>

            <table style={{ width: "100%", marginTop: "30px", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ width: "45%", verticalAlign: "top" }}>
                    <div style={{ borderTop: "2px solid #333", paddingTop: "10px", marginTop: "50px" }}>
                      <p style={{ fontWeight: "bold" }}>
                        {language === "fr" ? "PARTIE DIVULGUANTE I (ACQUIZGROUP)" : "DISCLOSING PARTY I (ACQUIZGROUP)"}
                      </p>
                      <SignatureBox>
                        <p><strong>Signature :</strong> <DataField>AcquizGroup</DataField></p>
                        <p><strong>{language === "fr" ? "Titre" : "Title"} :</strong> <DataField>{language === "fr" ? "Agent de liaison autorisé" : "Authorized Liaison Agent"}</DataField></p>
                        <p><strong>Date :</strong> <DataField>{data.generationDate}</DataField></p>
                      </SignatureBox>
                    </div>
                  </td>
                  <td style={{ width: "10%" }}></td>
                  <td style={{ width: "45%", verticalAlign: "top" }}>
                    <div style={{ borderTop: "2px solid #333", paddingTop: "10px", marginTop: "50px" }}>
                      <p style={{ fontWeight: "bold" }}>
                        {language === "fr" 
                          ? `PARTIE RÉCIPIENDAIRE (${recipientLabelUpper("fr", data.recipientType)})`
                          : `RECEIVING PARTY (${recipientLabelUpper("en", data.recipientType)})`}
                      </p>
                      <SignatureBox>
                        <p>
                          <strong>Signature :</strong>{" "}
                          {data.recipientSignature ? (
                            <img src={data.recipientSignature} alt="Signature" style={{ maxWidth: "200px", maxHeight: "60px" }} />
                          ) : (
                            language === "fr" ? "[En attente de signature]" : "[Awaiting signature]"
                          )}
                        </p>
                        <p><strong>{language === "fr" ? "Nom" : "Name"} :</strong> <DataField>{data.recipientName}</DataField></p>
                        <p><strong>Date :</strong> <DataField>{data.generationDate}</DataField></p>
                      </SignatureBox>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {data.hasBroker && (
              <div style={{ marginTop: "40px", borderTop: "1px solid #ddd", paddingTop: "20px" }}>
                <p style={{ fontWeight: "bold" }}>
                  {language === "fr" ? "PARTIE DIVULGUANTE II (LE BROKER)" : "DISCLOSING PARTY II (THE BROKER)"}
                </p>
                <div style={{ width: "45%" }}>
                  <SignatureBox>
                    <p><strong>Signature :</strong> {language === "fr" ? "[En attente de signature]" : "[Awaiting signature]"}</p>
                    <p><strong>{language === "fr" ? "Nom" : "Name"} :</strong> <DataField>{data.brokerName}</DataField></p>
                    <p><strong>Date :</strong> {language === "fr" ? "[En attente]" : "[Pending]"}</p>
                  </SignatureBox>
                </div>
              </div>
            )}

            <div style={{ marginTop: "40px", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "5px" }}>
              <p style={{ fontSize: "14px", margin: 0 }}><strong>🔐 {language === "fr" ? "Sécurité et Conformité" : "Security and Compliance"}</strong></p>
              <p style={{ fontSize: "12px", margin: "5px 0 0 0" }}>
                {language === "fr" 
                  ? "Ce document est généré automatiquement par la plateforme AcquizGroup. Toutes les signatures électroniques sont juridiquement valides conformément aux lois applicables."
                  : "This document is automatically generated by the AcquizGroup platform. All electronic signatures are legally valid in accordance with applicable laws."}
              </p>
            </div>

            {/* Client signature pad - inline at bottom of document */}
            {!isAdminOrStaff && (
              <div style={{ marginTop: "40px", padding: "24px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "2px solid #CEAB65" }}>
                <h3 style={{ marginTop: 0, marginBottom: "16px", color: "#1D314D", borderBottom: "2px solid #CEAB65", paddingBottom: "8px" }}>
                  {language === "fr" ? "Votre Signature Électronique" : "Your Electronic Signature"}
                </h3>
                <SignaturePadComponent onSignatureChange={handleSignatureChange} language={language} />
                
                <button
                  disabled={!isFormValid || !data.recipientSignature || saving}
                  onClick={handleSaveNDA}
                  style={{
                    width: "100%",
                    marginTop: "20px",
                    padding: "14px",
                    backgroundColor: isFormValid && data.recipientSignature && !saving ? "#28a745" : "#ccc",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isFormValid && data.recipientSignature && !saving ? "pointer" : "not-allowed",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  {saving 
                    ? (language === "fr" ? "Sauvegarde en cours..." : "Saving...") 
                    : (language === "fr" ? "Signer et Sauvegarder le NDA" : "Sign and Save NDA")}
                </button>
                
                {(!isFormValid || !data.recipientSignature) && (
                  <p style={{ fontSize: "12px", color: "#666", marginTop: "8px", textAlign: "center" }}>
                    {language === "fr" 
                      ? "Remplissez vos informations dans la section B et signez pour continuer" 
                      : "Fill in your information in section B and sign to continue"}
                  </p>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Admin signature is rendered inside the form column (see DataInputForm wrapper above)
            so it never covers the questionnaire. */}
      </div>
    </div>
  );
};

export default NDADocument;
